import { APIError, apiRequest } from "@/lib/api";
import type { AuthUser } from "@/lib/auth-context";
import { AUTH_USER_STORAGE_KEY, readStoredAuthSession, writeStoredAuthSession } from "@/lib/auth-storage";
import { verifyHostedAuthSession } from "@/lib/hosted-auth-callback";
import { resetWalletBetaDisclaimerAcceptance } from "@/lib/wallet-beta-disclaimer";

type HostedGoogleAuthInput = {
  idToken: string;
};

type HostedAuth0Input = {
  idToken: string;
  expectedEmail?: string;
  disallowedEmail?: string;
};

type HostedOAuthCodeInput = {
  code: string;
  codeVerifier: string;
  redirectURI: string;
};

type HostedAuthResponse = {
  user: AuthUser | null;
  session_token?: string;
  session_expires_at?: string;
  managed_session_error?: string;
};

type ManagedAuthMeResponse = {
  user?: Record<string, unknown> | null;
  session_expires_at?: string;
};

const DEFAULT_MANAGED_WALLET_API_BASE_URL = "https://genrl-wallet-api-production.up.railway.app";

function managedWalletAPIBaseURL(): string {
  return String(import.meta.env.VITE_API_BASE_URL || DEFAULT_MANAGED_WALLET_API_BASE_URL)
    .trim()
    .replace(/\/+$/, "");
}

async function exchangeManagedAuth0Session(input: HostedAuth0Input): Promise<HostedAuthResponse> {
  const baseURL = managedWalletAPIBaseURL();
  if (!baseURL) throw new Error("Managed wallet API is not configured.");

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(`${baseURL}/api/auth/hosted/auth0`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        idToken: input.idToken,
        expected_email: String(input.expectedEmail || "").trim().toLowerCase(),
        disallowed_email: String(input.disallowedEmail || "").trim().toLowerCase(),
      }),
      credentials: "include",
      signal: controller.signal,
    });
    const payload = await response.json().catch(() => ({})) as HostedAuthResponse & {
      error_code?: string;
      error_detail?: string;
    };
    if (!response.ok) {
      throw new APIError(
        response.status,
        String(payload.error_code || "ERR_AUTH0_LOGIN_FAILED"),
        String(payload.error_detail || `Managed wallet sign-in failed with HTTP ${response.status}.`),
      );
    }
    if (!payload?.user || !String(payload.session_token || "").trim()) {
      throw new Error("Managed wallet sign-in did not return a complete session.");
    }
    return payload;
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw new APIError(408, "ERR_TIMEOUT", "Managed wallet sign-in timed out.");
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export type HostedPasswordResetRequestResponse = {
  ok: boolean;
  delivery?: string;
  reset_token?: string;
  reset_url?: string;
};

export type HostedEmailVerificationResponse = {
  ok: boolean;
  verified?: boolean;
  delivery?: string;
  verification_token?: string;
  verification_url?: string;
  user?: AuthUser | null;
};

function authUserFromManagedUser(serverUser: Record<string, unknown> | null | undefined): AuthUser | null {
  const id = String(serverUser?.id || "").trim();
  if (!id) return null;
  const provider = String(serverUser?.provider || "auth0").trim().toLowerCase();
  const normalizedProvider: AuthUser["provider"] =
    provider === "google" ||
    provider === "github" ||
    provider === "auth0" ||
    provider === "apple" ||
    provider === "email"
      ? provider
      : "auth0";
  return {
    id,
    username: String(serverUser?.username || "").trim() || undefined,
    email: String(serverUser?.email || "").trim(),
    name: String(serverUser?.name || serverUser?.username || serverUser?.email || "Wallet Owner").trim(),
    avatarUrl: String(serverUser?.avatarUrl || serverUser?.avatar_url || "").trim() || undefined,
    provider: normalizedProvider,
  };
}

async function readManagedSessionUser(): Promise<AuthUser | null> {
  const session = readStoredAuthSession();
  try {
    const payload = await apiRequest<ManagedAuthMeResponse>("/api/auth/managed/me", {
      ...(session?.token ? { headers: { Authorization: `Bearer ${session.token}` } } : {}),
      timeoutMs: 10000,
      cache: "no-store",
    });
    const user = authUserFromManagedUser(payload?.user || null);
    if (!user) return null;
    if (session?.token) {
      writeStoredAuthSession({
        token: session.token,
        expiresAt: String(payload?.session_expires_at || session.expiresAt || ""),
      });
    }
    return user;
  } catch (error) {
    if (error instanceof APIError && (error.status === 401 || error.status === 403)) {
      writeStoredAuthSession(null);
      return null;
    }
    throw error;
  }
}

export async function hostedReadSessionUser(): Promise<AuthUser | null> {
  let hostedUser: AuthUser | null = null;
  let hostedError: unknown;
  try {
    const payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/me", {
      timeoutMs: 12000,
    });
    hostedUser = payload?.user || null;
  } catch (error) {
    hostedError = error;
  }

  try {
    const managedUser = await readManagedSessionUser();
    if (managedUser) return managedUser;
  } catch (error) {
    if (!hostedUser) throw error;
  }

  // The hosted profile cookie is not authorization for wallet data. Only a
  // managed backend session may admit the user to the application.
  if (hostedUser) return null;
  if (hostedError) throw hostedError;
  return null;
}

function persistManagedWalletSession(payload: HostedAuthResponse) {
  const token = String(payload?.session_token || "").trim();
  const expiresAt = String(payload?.session_expires_at || "").trim();
  if (!token) {
    writeStoredAuthSession(null);
    return;
  }
  writeStoredAuthSession({
    token,
    expiresAt,
  });
}

function persistHostedAuthUser(user: AuthUser | null | undefined) {
  if (typeof window === "undefined") return;
  if (!user) {
    window.localStorage.removeItem(AUTH_USER_STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
}

export async function hostedSignupWithPassword(input: {
  username: string;
  email: string;
  name: string;
  password: string;
}): Promise<AuthUser> {
  const payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/signup", {
    method: "POST",
    body: input,
    timeoutMs: 20000,
  });
  if (!payload?.user) throw new Error("Hosted signup did not return a user.");
  persistManagedWalletSession(payload);
  resetWalletBetaDisclaimerAcceptance();
  return payload.user;
}

export async function hostedLoginWithPassword(input: {
  identifier: string;
  password?: string;
}): Promise<AuthUser> {
  const payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/login", {
    method: "POST",
    body: input,
    timeoutMs: 20000,
  });
  if (!payload?.user) throw new Error("Hosted login did not return a user.");
  persistManagedWalletSession(payload);
  resetWalletBetaDisclaimerAcceptance();
  return payload.user;
}

export async function hostedLoginWithGoogle(input: HostedGoogleAuthInput): Promise<AuthUser> {
  const payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/google", {
    method: "POST",
    body: input,
    timeoutMs: 20000,
  });
  if (!payload?.user) throw new Error("Hosted Google login did not return a user.");
  persistManagedWalletSession(payload);
  resetWalletBetaDisclaimerAcceptance();
  return payload.user;
}

export async function hostedLoginWithGoogleCode(input: HostedOAuthCodeInput): Promise<AuthUser> {
  const payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/google/exchange", {
    method: "POST",
    body: {
      code: input.code,
      code_verifier: input.codeVerifier,
      redirect_uri: input.redirectURI,
    },
    timeoutMs: 20000,
  });
  if (!payload?.user) throw new Error("Hosted Google login did not return a user.");
  persistManagedWalletSession(payload);
  resetWalletBetaDisclaimerAcceptance();
  return payload.user;
}

export async function hostedLoginWithGitHub(input: HostedOAuthCodeInput): Promise<AuthUser> {
  const payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/github", {
    method: "POST",
    body: {
      code: input.code,
      code_verifier: input.codeVerifier,
      redirect_uri: input.redirectURI,
    },
    timeoutMs: 20000,
  });
  if (!payload?.user) throw new Error("Hosted GitHub login did not return a user.");
  persistManagedWalletSession(payload);
  resetWalletBetaDisclaimerAcceptance();
  return payload.user;
}

export async function hostedLoginWithAuth0(input: HostedAuth0Input): Promise<AuthUser> {
  let payload: HostedAuthResponse | null = null;
  let bridgeError: unknown;
  try {
    payload = await apiRequest<HostedAuthResponse>("/api/auth/hosted/auth0", {
      method: "POST",
      body: {
        idToken: input.idToken,
        expected_email: String(input.expectedEmail || "").trim().toLowerCase(),
        disallowed_email: String(input.disallowedEmail || "").trim().toLowerCase(),
      },
      timeoutMs: 20000,
    });
  } catch (error) {
    bridgeError = error;
  }

  if (!payload?.user || !String(payload.session_token || "").trim()) {
    try {
      payload = await exchangeManagedAuth0Session(input);
    } catch (managedError) {
      if (bridgeError) throw bridgeError;
      if (payload?.managed_session_error) {
        throw new Error(`Managed wallet session was not created: ${payload.managed_session_error}`);
      }
      throw managedError;
    }
  }

  if (!payload?.user) throw new Error("Hosted Auth0 login did not return a user.");
  const issuedSessionToken = String(payload.session_token || "").trim();
  persistManagedWalletSession(payload);
  persistHostedAuthUser(payload.user);
  try {
    const sessionUser = await verifyHostedAuthSession(payload.user, hostedReadSessionUser);
    persistHostedAuthUser(sessionUser);
    resetWalletBetaDisclaimerAcceptance();
    return sessionUser;
  } catch (error) {
    await hostedLogout({
      timeoutMs: 5000,
      sessionToken: issuedSessionToken,
    }).catch(() => undefined);
    writeStoredAuthSession(null);
    persistHostedAuthUser(null);
    throw error;
  }
}

export async function hostedLogout({
  timeoutMs = 10000,
  sessionToken = "",
}: {
  timeoutMs?: number;
  sessionToken?: string;
} = {}): Promise<void> {
  const session = readStoredAuthSession();
  const token = String(sessionToken || session?.token || "").trim();
  let managedFailure: unknown;
  try {
    await apiRequest("/api/auth/managed/logout", {
      method: "POST",
      ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
      timeoutMs,
    });
  } catch (error) {
    managedFailure = error;
  }
  let hostedFailure: unknown;
  try {
    await apiRequest("/api/auth/hosted/logout", {
      method: "POST",
      timeoutMs,
    });
  } catch (error) {
    hostedFailure = error;
  }
  if (managedFailure) throw managedFailure;
  if (hostedFailure) throw hostedFailure;
}

export async function hostedRequestPasswordReset(input: {
  email: string;
}): Promise<HostedPasswordResetRequestResponse> {
  return apiRequest<HostedPasswordResetRequestResponse>("/api/auth/hosted/password-reset/request", {
    method: "POST",
    body: input,
    timeoutMs: 15000,
  });
}

export async function hostedConfirmPasswordReset(input: {
  token: string;
  password: string;
}): Promise<void> {
  await apiRequest("/api/auth/hosted/password-reset/confirm", {
    method: "POST",
    body: input,
    timeoutMs: 15000,
  });
}

export async function hostedRequestEmailVerification(): Promise<HostedEmailVerificationResponse> {
  return apiRequest<HostedEmailVerificationResponse>("/api/auth/hosted/email-verification/request", {
    method: "POST",
    body: {},
    timeoutMs: 15000,
  });
}

export async function hostedConfirmEmailVerification(input: {
  token: string;
}): Promise<HostedEmailVerificationResponse> {
  return apiRequest<HostedEmailVerificationResponse>("/api/auth/hosted/email-verification/confirm", {
    method: "POST",
    body: input,
    timeoutMs: 15000,
  });
}
